'use client';

import React, { useEffect, useState } from 'react';
import { empresaService } from '@/lib/api/empresaService';
import { Empresa } from '@/types/empresa';
import { EmpresaTable } from './EmpresaTable';
import { EmpresaFormModal } from './EmpresaFormModal';
import { EmpresaDetail } from './EmpresaDetail';
import { Button } from '@/components/ui/Button';

// Icons
const SearchIcon = () => (
  <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
  </svg>
);

const EmpresaManagement: React.FC = () => {
  const [empresas, setEmpresas] = useState<Empresa[]>([]);
  const [filtered, setFiltered] = useState<Empresa[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [currentEmpresa, setCurrentEmpresa] = useState<Partial<Empresa>>({});
  const [isDetailOpen, setIsDetailOpen] = useState(false);
  const [selectedEmpresa, setSelectedEmpresa] = useState<Empresa | null>(null);

  const fetchEmpresas = async () => {
    setLoading(true);
    try {
      const response = await empresaService.getAll();
      if (response.data) {
        // Ordenar alfabéticamente por nombre
        const sorted = [...response.data].sort((a, b) =>
          (a.nombre || '').localeCompare(b.nombre || '', 'es')
        );
        setEmpresas(sorted);
        setFiltered(sorted);
      } else {
        setError(response.error || 'Error al cargar empresas');
      }
    } catch (err) {
      setError('Error de conexión');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchEmpresas();
  }, []);

  useEffect(() => {
    const term = searchTerm.toLowerCase();
    const results = empresas.filter(e =>
      e.nombre.toLowerCase().includes(term) ||
      (e.rfc || '').toLowerCase().includes(term) ||
      (e.asesorNombre || '').toLowerCase().includes(term)
    );
    setFiltered(results);
  }, [searchTerm, empresas]);

  const handleCreate = () => {
    setCurrentEmpresa({});
    setIsModalOpen(true);
  };

  const handleView = (empresa: Empresa) => {
    setSelectedEmpresa(empresa);
    setIsDetailOpen(true);
  };

  const handleSubmit = async (data: Partial<Empresa>) => {
    // Crear vs editar según si abrimos el modal con una empresa existente (currentEmpresa.id),
    // no según data.id: en nueva empresa el modal ya pone data.id = Date.now() y eso haría que se llamara update por error
    const isEditing = Boolean(currentEmpresa?.id);
    const resp = isEditing && data.id
      ? await empresaService.update(data.id, data)
      : await empresaService.create(data);
    if (resp.error || !resp.data) {
      alert(resp.error || 'Error al guardar la empresa');
      return;
    }
    setIsModalOpen(false);
    setCurrentEmpresa({});
    fetchEmpresas();
  };

  if (loading && empresas.length === 0) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 bg-white p-6 rounded-xl shadow-sm border border-gray-100">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Catálogo de Empresas</h1>
        </div>
        <Button onClick={handleCreate} icon={<span className="text-lg font-bold">+</span>}>
          Nueva Empresa
        </Button>
      </div>

      {/* Filters */}
      <div className="bg-white p-4 rounded-xl shadow-sm border border-gray-100">
        <div className="relative max-w-md">
          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
            <SearchIcon />
          </div>
          <input
            type="text"
            className="block w-full pl-10 pr-3 py-2.5 border border-gray-300 rounded-lg leading-5 bg-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 sm:text-sm transition-all"
            placeholder="Buscar por nombre, region..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
      </div>

      {/* Content */}
      {error ? (
        <div className="p-4 bg-red-50 text-red-700 rounded-lg border border-red-200">
          <p className="font-bold">Error</p>
          <p>{error}</p>
        </div>
      ) : (
        <>
          <EmpresaTable empresas={filtered} onView={handleView} />
          <div className="text-right text-xs text-gray-400 px-2">
            Total: {filtered.length} empresas
          </div>
        </>
      )}

      <EmpresaFormModal 
        isOpen={isModalOpen} 
        onClose={() => setIsModalOpen(false)}
        onSubmit={handleSubmit}
        initialData={currentEmpresa}
      />

      <EmpresaDetail
        isOpen={isDetailOpen}
        onClose={() => setIsDetailOpen(false)}
        empresa={selectedEmpresa}
        onUpdate={(updated) => {
          setSelectedEmpresa(updated);
          fetchEmpresas();
        }}
      />
    </div>
  );
};

export default EmpresaManagement;
